import { useMemo, useState } from "react";
import {
  ChevronLeft, ChevronRight, Calendar, Truck, Package, Wrench,
  Users, FileText, ShieldCheck, Flag, Rocket, CheckCircle2,
  Boxes, Zap, RadioTower,
} from "lucide-react";
import { Button } from "@/components/ui";
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays,
  addMonths, isSameMonth, parseISO, isToday, differenceInCalendarDays,
} from "date-fns";

// =============================================================================
// CalendarMonth — month grid view of project bookings + milestones.
// T-065 partial — month view (week + day views can land later if needed).
// T-079 (2026-04-25) — render project_milestones alongside bookings.
// 2026-04-27 — multi-day bookings now render as ONE continuous bar per week
//   using a per-week absolute overlay grid with greedy lane assignment so
//   overlapping bookings stack vertically. Single-day bookings use the same
//   path with span = 1. Per-day pills (milestones, project markers, "+N
//   more" overflow) still live inside each day cell, below the bars area.
// =============================================================================

// Every member of the booking_type enum, and it has to STAY every member. This
// list has drifted twice: materials and electrical were added to the database
// and never added here, and outage went the same way in migration 325. The
// symptom is not a crash - it is a booking that quietly renders as a grey
// wrench on the project calendar, and a Record<BookingType, …> below that stops
// compiling the moment somebody widens the enum.
type BookingType =
  | "plant" | "materials" | "delivery" | "electrical" | "integration"
  | "subcontractor" | "meeting" | "inspection" | "outage" | "other";

interface Booking {
  booking_id:      string;
  booking_type:    BookingType;
  title:           string;
  scheduled_start: string;
  scheduled_end:   string | null;
  status:          string;
}

interface Milestone {
  milestone_id:   string;
  milestone_name: string;
  milestone_date: string;   // yyyy-MM-dd
}

// ProjectDates — optional. Renders project scheduled/actual start+end as
// distinct pills on the calendar so the project's overall shape (start /
// duration / end) is visible alongside bookings + milestones. Each is a
// single-day pill on its specific date.
export interface ProjectDates {
  scheduledStart: string | null;   // yyyy-MM-dd
  scheduledEnd:   string | null;
  actualStart:    string | null;
  actualEnd:      string | null;
}

const TYPE_COLOUR: Record<BookingType, string> = {
  plant:         "#60A5FA",  // blue
  materials:     "#34D399",  // emerald
  delivery:      "#FBBF24",  // amber
  electrical:    "#FACC15",  // yellow
  integration:   "#A78BFA",  // purple
  subcontractor: "#EC4899",  // pink
  meeting:       "#94A3B8",  // slate
  inspection:    "#6B7280",  // grey
  outage:        "#7C3AED",  // violet — same purple a promoted key date wears
                             // on the company schedule, deliberately, so an
                             // outage looks like an outage on every screen
  other:         "#475569",  // dark slate
};

// Milestones use the brand accent so they pop against the booking palette.
const MILESTONE_COLOUR = "#F97316";  // orange (accent)

// Project span markers — distinct from milestones so the project's
// scheduled/actual book-ends are visually separable.
const PROJECT_DATE_COLOUR_SCHEDULED = "#10B981";  // green — planning
const PROJECT_DATE_COLOUR_ACTUAL    = "#22C55E";  // brighter green — reality

const TYPE_ICON: Record<BookingType, any> = {
  plant: Truck, materials: Boxes, delivery: Package, electrical: Zap,
  integration: ShieldCheck, subcontractor: Users, meeting: Calendar,
  inspection: FileText, outage: RadioTower, other: Wrench,
};

// Layout constants — kept here so the day-cell padding-top reservation
// and the bar grid offset match exactly. Change one, change all.
const DAY_NUMBER_HEIGHT_PX = 24;   // top space inside each cell for the date pill
const BAR_HEIGHT_PX        = 14;
const BAR_GAP_PX           = 2;
const LANE_HEIGHT_PX       = BAR_HEIGHT_PX + BAR_GAP_PX;   // 16
const LANES_PADDING_PX     = 4;    // breathing room between bars area + per-day pills
const MAX_LANES            = 4;    // bookings beyond this fall back to "+N more"

interface Props {
  bookings: Booking[];
  milestones?: Milestone[];
  projectDates?: ProjectDates;
  onBookingClick: (bookingId: string) => void;
}

// Internal "project date marker" type — flatten the four optional dates
// into a uniform shape so day-cells render them with the same code path
// as milestones.
interface ProjectDateMarker {
  key:    string;     // yyyy-MM-dd
  label:  string;     // "Scheduled start", etc.
  kind:   "scheduled" | "actual";
  icon:   typeof Rocket;
}

// One booking bar slotted into a week. dayIndex is 0..6 (Mon..Sun) for
// the leftmost visible day; span is the column count to span; lane is
// the 0-based vertical slot within the week's bar overlay.
interface WeekBar {
  booking:    Booking;
  dayIndex:   number;
  span:       number;
  startsHere: boolean;   // booking actually starts this week (not a continuation)
  endsHere:   boolean;   // booking actually ends this week
  lane:       number;
}

// -----------------------------------------------------------------------------

export default function CalendarMonth({ bookings, milestones = [], projectDates, onBookingClick }: Props) {
  const [viewMonth, setViewMonth] = useState<Date>(() => startOfMonth(new Date()));

  // Build the 6-week grid (start week containing month start, end week
  // containing month end). Group into [week][day] for per-week rendering.
  const weeks = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1 });   // Mon
    const end   = endOfWeek(endOfMonth(viewMonth),   { weekStartsOn: 1 });
    const out: Date[] = [];
    let d = start;
    while (d <= end) {
      out.push(d);
      d = addDays(d, 1);
    }
    while (out.length < 42) {
      out.push(addDays(out[out.length - 1], 1));
    }
    const trimmed = out.slice(0, 42);
    const grouped: Date[][] = [];
    for (let i = 0; i < 6; i++) grouped.push(trimmed.slice(i * 7, (i + 1) * 7));
    return grouped;
  }, [viewMonth]);

  // Per-week: compute booking bars (one per booking that intersects the
  // week, clipped to week boundaries) and assign lanes greedily so
  // overlapping bookings stack vertically. Bars beyond MAX_LANES are
  // dropped from the bar layer and counted as overflow on each day they
  // would have covered, so cells can flag "+N more".
  const weekBars = useMemo(() => {
    return weeks.map(weekDays => {
      const weekStart = weekDays[0];
      const weekEnd   = weekDays[6];
      const weekStartKey = format(weekStart, "yyyy-MM-dd");
      const weekEndKey   = format(weekEnd,   "yyyy-MM-dd");

      type Tmp = WeekBar & { startKey: string; endKey: string };
      const tmps: Tmp[] = [];

      for (const b of bookings) {
        const bStartKey = format(parseISO(b.scheduled_start), "yyyy-MM-dd");
        const bEndKey   = b.scheduled_end
          ? format(parseISO(b.scheduled_end), "yyyy-MM-dd")
          : bStartKey;

        if (bEndKey < weekStartKey || bStartKey > weekEndKey) continue;

        const visStart = bStartKey < weekStartKey ? weekStartKey : bStartKey;
        const visEnd   = bEndKey   > weekEndKey   ? weekEndKey   : bEndKey;
        const dayIndex = differenceInCalendarDays(parseISO(visStart), weekStart);
        const span     = differenceInCalendarDays(parseISO(visEnd),   parseISO(visStart)) + 1;

        tmps.push({
          booking:    b,
          dayIndex,
          span,
          startsHere: bStartKey >= weekStartKey,
          endsHere:   bEndKey   <= weekEndKey,
          lane:       0,
          startKey:   visStart,
          endKey:     visEnd,
        });
      }

      // Sort: earliest start first, then longest span first for stable packing.
      tmps.sort((a, z) => a.startKey.localeCompare(z.startKey) || z.span - a.span);

      // Greedy lane assignment.
      const laneEnds: string[] = [];
      const placed: WeekBar[] = [];
      const overflowTmps: Tmp[] = [];

      for (const t of tmps) {
        let lane = -1;
        for (let i = 0; i < laneEnds.length; i++) {
          if (laneEnds[i] < t.startKey) { lane = i; break; }
        }
        if (lane === -1) lane = laneEnds.length;

        if (lane >= MAX_LANES) {
          overflowTmps.push(t);
        } else {
          laneEnds[lane] = t.endKey;
          placed.push({
            booking:    t.booking,
            dayIndex:   t.dayIndex,
            span:       t.span,
            startsHere: t.startsHere,
            endsHere:   t.endsHere,
            lane,
          });
        }
      }

      // Per-day overflow counts — only for bars that didn't fit.
      const overflowByDayIndex = new Array(7).fill(0) as number[];
      for (const t of overflowTmps) {
        for (let i = t.dayIndex; i < t.dayIndex + t.span; i++) {
          overflowByDayIndex[i] += 1;
        }
      }

      const laneCount = Math.min(laneEnds.length, MAX_LANES);
      return { bars: placed, laneCount, overflowByDayIndex };
    });
  }, [weeks, bookings]);

  // Index milestones by day-key. milestone_date is already yyyy-MM-dd.
  const milestonesByDay = useMemo(() => {
    const m = new Map<string, Milestone[]>();
    for (const ms of milestones) {
      const arr = m.get(ms.milestone_date) ?? [];
      arr.push(ms);
      m.set(ms.milestone_date, arr);
    }
    for (const arr of m.values()) {
      arr.sort((a, z) => a.milestone_name.localeCompare(z.milestone_name));
    }
    return m;
  }, [milestones]);

  // Index project date markers by day-key.
  const projectMarkersByDay = useMemo(() => {
    const m = new Map<string, ProjectDateMarker[]>();
    if (!projectDates) return m;
    function add(date: string | null, label: string, kind: "scheduled" | "actual", icon: typeof Rocket) {
      if (!date) return;
      const arr = m.get(date) ?? [];
      arr.push({ key: date, label, kind, icon });
      m.set(date, arr);
    }
    add(projectDates.scheduledStart, "Scheduled start", "scheduled", Rocket);
    add(projectDates.scheduledEnd,   "Scheduled end",   "scheduled", CheckCircle2);
    add(projectDates.actualStart,    "Actual start",    "actual",    Rocket);
    add(projectDates.actualEnd,      "Actual end",      "actual",    CheckCircle2);
    return m;
  }, [projectDates]);

  return (
    <div>
      {/* Month nav header */}
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setViewMonth(addMonths(viewMonth, -1))}
            className="w-8 h-8 rounded border border-line text-text-muted hover:border-accent hover:text-accent inline-flex items-center justify-center transition-colors"
            aria-label="Previous month"
          >
            <ChevronLeft size={14}/>
          </button>
          <div className="text-[16px] font-semibold text-text min-w-[160px] text-center">
            {format(viewMonth, "MMMM yyyy")}
          </div>
          <button
            type="button"
            onClick={() => setViewMonth(addMonths(viewMonth, 1))}
            className="w-8 h-8 rounded border border-line text-text-muted hover:border-accent hover:text-accent inline-flex items-center justify-center transition-colors"
            aria-label="Next month"
          >
            <ChevronRight size={14}/>
          </button>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setViewMonth(startOfMonth(new Date()))}
        >
          Today
        </Button>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-px bg-line border border-line rounded-t overflow-hidden">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(d => (
          <div key={d} className="bg-ink-raised text-[10px] font-mono uppercase tracking-wider2 text-text-muted text-center py-1.5">
            {d}
          </div>
        ))}
      </div>

      {/* Week rows — flex column with 1px gaps for the horizontal grid lines */}
      <div className="flex flex-col gap-px bg-line border-l border-r border-b border-line rounded-b overflow-hidden">
        {weeks.map((weekDays, wi) => {
          const { bars, laneCount, overflowByDayIndex } = weekBars[wi];
          const lanesAreaPx = laneCount > 0
            ? (laneCount * LANE_HEIGHT_PX) + LANES_PADDING_PX
            : 0;
          const cellPaddingTopPx = DAY_NUMBER_HEIGHT_PX + lanesAreaPx;

          return (
            <div key={wi} className="relative grid grid-cols-7 gap-px">
              {/* Day cells — backgrounds, day number, per-day pills */}
              {weekDays.map((day, di) => {
                const inMonth = isSameMonth(day, viewMonth);
                const today   = isToday(day);
                const key     = format(day, "yyyy-MM-dd");
                const dayMilestones     = milestonesByDay.get(key) ?? [];
                const dayProjectMarkers = projectMarkersByDay.get(key) ?? [];
                const overflow          = overflowByDayIndex[di];

                // Build-duration tint — T-081. Days inside the project's
                // scheduled span get a soft green wash so the project's
                // overall shape reads at a glance.
                const inScheduledSpan = !!(
                  projectDates?.scheduledStart &&
                  projectDates?.scheduledEnd &&
                  key >= projectDates.scheduledStart &&
                  key <= projectDates.scheduledEnd
                );
                const cellBg = inScheduledSpan ? "bg-ok/[0.08]" : "bg-ink";

                return (
                  <div
                    key={di}
                    className={`${cellBg} relative min-h-[110px] px-1.5 pb-1.5 ${inMonth ? "" : "opacity-40"}`}
                    style={{ paddingTop: `${cellPaddingTopPx}px` }}
                  >
                    <div className={`absolute top-1.5 left-1.5 text-[11px] font-mono inline-flex items-center justify-center w-5 h-5 rounded-full ${
                      today ? "bg-accent text-white font-semibold" : inMonth ? "text-text" : "text-text-dim"
                    }`}>
                      {format(day, "d")}
                    </div>

                    <div className="space-y-0.5">
                      {/* Project date markers — green pills (scheduled vs actual). */}
                      {dayProjectMarkers.map((mk, i) => {
                        const Icon = mk.icon;
                        const colour = mk.kind === "actual"
                          ? PROJECT_DATE_COLOUR_ACTUAL
                          : PROJECT_DATE_COLOUR_SCHEDULED;
                        return (
                          <div
                            key={`${mk.key}-${i}`}
                            className="px-1.5 py-0.5 rounded text-[10px] text-white truncate inline-flex items-center gap-1 w-full"
                            style={{ background: colour }}
                            title={`Project — ${mk.label}`}
                          >
                            <Icon size={9} className="flex-shrink-0"/>
                            <span className="truncate">{mk.label}</span>
                          </div>
                        );
                      })}
                      {/* Milestones — accent-coloured pills with flag icon. */}
                      {dayMilestones.map(ms => (
                        <div
                          key={ms.milestone_id}
                          className="px-1.5 py-0.5 rounded text-[10px] text-white truncate inline-flex items-center gap-1 w-full"
                          style={{ background: MILESTONE_COLOUR }}
                          title={`Milestone — ${ms.milestone_name}`}
                        >
                          <Flag size={9} className="flex-shrink-0"/>
                          <span className="truncate">{ms.milestone_name}</span>
                        </div>
                      ))}
                      {overflow > 0 && (
                        <div className="text-[9px] text-text-muted px-1.5">
                          +{overflow} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Booking bar overlay — single grid layered above the day
                  cells. Each bar uses grid-column: start / span N so it
                  renders as ONE continuous bar across multiple days; the
                  1px gap between cells gets covered by the bar so the
                  visual is unbroken. Lanes give vertical stacking when
                  bookings overlap. */}
              <div
                className="absolute left-0 right-0 grid grid-cols-7 gap-px pointer-events-none"
                style={{ top: `${DAY_NUMBER_HEIGHT_PX}px` }}
              >
                {bars.map(bar => {
                  const labelStr = `${format(parseISO(bar.booking.scheduled_start), "HH:mm")} ${bar.booking.title}`;
                  const tipEnd = bar.booking.scheduled_end
                    ? ` → ${format(parseISO(bar.booking.scheduled_end), "dd MMM HH:mm")}`
                    : "";
                  // Round only the edges that are real start/end. Bars
                  // crossing into the next week stay square on the cut
                  // side so they read as continuing.
                  const roundLeft  = bar.startsHere ? "rounded-l" : "";
                  const roundRight = bar.endsHere   ? "rounded-r" : "";
                  return (
                    <button
                      key={`${bar.booking.booking_id}-w${wi}`}
                      type="button"
                      onClick={() => onBookingClick(bar.booking.booking_id)}
                      style={{
                        gridColumn: `${bar.dayIndex + 1} / span ${bar.span}`,
                        gridRow: 1,
                        marginTop: `${bar.lane * LANE_HEIGHT_PX}px`,
                        height: `${BAR_HEIGHT_PX}px`,
                        background: TYPE_COLOUR[bar.booking.booking_type],
                      }}
                      className={`pointer-events-auto ${roundLeft} ${roundRight} text-[10px] leading-[14px] text-white truncate hover:opacity-80 transition-opacity px-1.5 mx-0.5 text-center font-medium`}
                      title={`${format(parseISO(bar.booking.scheduled_start), "dd MMM HH:mm")}${tipEnd} · ${bar.booking.title}`}
                    >
                      {labelStr}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-3 flex items-center gap-3 flex-wrap text-[10px]">
        {projectDates && (
          <>
            <div className="inline-flex items-center gap-1">
              <Rocket size={10} style={{ color: PROJECT_DATE_COLOUR_SCHEDULED }}/>
              <span className="text-text-muted">project (sched / actual)</span>
            </div>
          </>
        )}
        <div className="inline-flex items-center gap-1">
          <Flag size={10} style={{ color: MILESTONE_COLOUR }}/>
          <span className="text-text-muted">milestone</span>
        </div>
        {(Object.entries(TYPE_COLOUR) as Array<[BookingType, string]>).map(([type, colour]) => {
          const Icon = TYPE_ICON[type];
          return (
            <div key={type} className="inline-flex items-center gap-1">
              <Icon size={10} style={{ color: colour }}/>
              <span className="text-text-muted capitalize">{type}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
